// Home.tsx
import React, { useEffect, useState } from 'react';
import { Anchor, Badge, Center, Modal, Stack, Tabs, Text } from '@mantine/core';

import API, { PUBLIC_URL } from '../utils/const';
import { Account, Calendar } from '../components/AccountCard';
import LoadingOverlay from '../components/LoadingOverlay';
import { createToast } from '../components/Toast';
import { MessageKind, redirectWithParameters } from '../utils/common';
import { useTranslation } from 'react-i18next';
import AppLayout from '../layouts/AppLayout';
import { getSearchParam } from '../utils/app';
import { Head } from 'vite-react-ssg';
import { useAuth } from '../contexts/AuthContext';
import { useFathom } from '../contexts/FathomContext';
import { SynchronizationsTab } from '../components/SynchronizationsTab';
import { AccountTab } from '../components/AccountTab';
import { IconArrowsRightLeft, IconCalendar, IconUser } from '@tabler/icons-react';
import { AddCalendarAccountWrapper } from '../components/AddCalendarAccount';
import LoginCard from '../components/LoginCard';
import { ScopeErrorModal } from '../components/ScopeErrorModal';
import { ScheduleTab } from '../components/schedule/ScheduleTab';



const InnerDashboard: React.FC = () => {
  const { t } = useTranslation(['app']);

  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [daysLeft, setDaysLeft] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<string | null>('synchronizations');

  const { user, setRedirectOnUnauthenticated, isAuthenticated } = useAuth();
  const { trackEvent } = useFathom();

  useEffect(() => {
    setRedirectOnUnauthenticated(false);
  }, [])

  // const [openDraft, setOpenDraft] = useState<boolean>(false)

  // Render AccountCards for each account
  const [showOnboarding, setShowOnboarding] = useState<boolean>(false);
  const [magicValue, setMagicValue] = useState<string | null>(null);

  const queryParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const [errorParam, _] = useState(queryParams.get('error'));

  const handleMagicLink = async (_magicValue: string) => {
    window.location.href = `${API}/magic-link/${_magicValue}`
  }

  const fetchAccountsData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/accounts`, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        createToast(error.message || t("common.internal-server-error"), MessageKind.Error);

        throw new Error(`${t('dashboard.error.fetching-account')}: ${error.message}`);
      }
      let accounts = await response.json() as Account[];
      let promises = []
      for (let i = 0; i < accounts.length; i++) {
        promises.push(fetchCalendars(accounts[i].uuid));
      }
      let calendars = await Promise.all(promises);
      for (let i = 0; i < accounts.length; i++) {
        accounts[i].calendars = calendars[i];
      }
      return accounts; // Assuming the data is an array of Account objects
    } finally {
      setLoading(false);
    }
  };

  // Fetch accounts data
  const fetchAccountData = async () => {
    try {
      const accountsData = await fetchAccountsData();
      setAccounts(accountsData);
      setAccountsLoaded(true);
    } finally {
      setLoading(false);  // Set loading to false after fetch completes
    }
  };

  useEffect(() => {
    setLoading(true);
  }, []);

  useEffect(() => {
    if (errorParam === 'scope-error') {
      setActiveTab('accounts');
      return;
    }

    if (accounts.length > 0) {
      setActiveTab("synchronizations")
    } else {
      setActiveTab("accounts")
    }
  }, [accounts, errorParam])

  useEffect(() => {
    const magicValue = getSearchParam('magic');
    setMagicValue(magicValue);
  }, []);

  useEffect(() => {
    if (!magicValue) { return; }
    handleMagicLink(magicValue);
  }, [magicValue])

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    // Calculate days left if user has no customer_id
    if (user.customer_id == null) {
      const currentDate = new Date();
      let targetDate = null;
      if (user.end_of_trial == null) {
        targetDate = user.date_created.getTime() + 24 * 60 * 60 * 1000 * 7;
      } else {
        targetDate = user.end_of_trial.getTime();
      }
      const timeDifference = targetDate - currentDate.getTime();
      const daysDifference = Math.ceil(timeDifference / (24 * 60 * 60 * 1000));
      setDaysLeft(daysDifference);
    }

    fetchAccountData();
  }, [user]); // Only user in dependencies

  useEffect(() => {
    if (accountsLoaded === true && accounts.length === 0 && showOnboarding === false) {
      setShowOnboarding(true);
    }
  }, [accountsLoaded, accounts, showOnboarding])



  async function fetchCalendars(uuid: string) {
    try {
      const response = await fetch(
        `${API}/accounts/${uuid}/calendars`,
        {
          method: 'GET',
          credentials: 'include'
        }
      );
      return await response.json() as Calendar[];
    } catch (error) {
      console.error(`${t("dashboard.error.fetching-calendars")}:`, error);
      return null
    }
  }

  async function updateAccounts() {
    const accountsData = await fetchAccountsData();
    setAccounts(accountsData);
  }

  const loginClicked = () => {
    trackEvent('Dashboard > Login Clicked')
    redirectWithParameters('login', { login: 'true' }, true)
  }

  const openTab = (tab: "accounts" | "synchronizations") => {
    setActiveTab(tab)
  }

  return (
    <>
      <div className='container col-xxl-8'>
        {(loading || isAuthenticated == null) && <LoadingOverlay />}
        <ScopeErrorModal defaultOpened={errorParam === 'scope-error'} />
        {isAuthenticated != null &&
          <>
            {isAuthenticated === false &&
              <Modal
                withCloseButton={false}
                opened={true}
                onClose={() => { }}
                transitionProps={{ transition: 'fade-up', duration: 600 }}
                overlayProps={{
                  backgroundOpacity: 0.55,
                  blur: 3,
                }}
              >
                <LoginCard />
              </Modal>
            }
            {isAuthenticated == false &&
              <div className='alert alert-light py-2 mt-4 border-2'>
                {t("dashboard.already-have-account")} <Anchor onClick={loginClicked}>{t("dashboard.login")}</Anchor>
              </div>
            }
            {user != null && user.customer_id == null &&
              <div className='container-sm p-0 my-2'>
                {daysLeft < 0 &&
                  <p className='p-0 m-0 text-danger'> {t("dashboard.trial-ended")} </p>
                }
                {daysLeft >= 0 &&
                  <p className='p-0 m-0'>{t("dashboard.days-left").replace("DAYS", daysLeft.toString())}</p>
                }
                <a className='m-0 p-0' href={`${PUBLIC_URL}/plan`}>{t("dashboard.upgrade")}</a>
              </div>
            }
            {accountsLoaded && (
              <>
                {accounts && accounts.length > 0 ? (
                  <Tabs onChange={setActiveTab} value={activeTab} mt={{ base: 'sm', md: 'lg' }}>
                    <Tabs.List>
                      <Tabs.Tab value="synchronizations" leftSection={<IconArrowsRightLeft size={16} />}>
                        <Text hiddenFrom="xs" size="xs">{t("dashboard.tab.synchronizations")}</Text>
                        <Text visibleFrom="xs" size="md">{t("dashboard.tab.synchronizations")}</Text>
                      </Tabs.Tab>

                      <Tabs.Tab value="schedules" leftSection={<><Badge size='xs' me='4' radius='sm' variant='light'>Beta</Badge><IconCalendar size={16} /></>}>
                        <Text hiddenFrom="xs" size="xs">{t("dashboard.tab.schedules")}</Text>
                        <Text visibleFrom="xs" size="md">{t("dashboard.tab.schedules")}</Text>
                      </Tabs.Tab>
                      <Tabs.Tab value="accounts" leftSection={<IconUser size={16} />}>
                        <Text hiddenFrom="xs" size="xs">{t("dashboard.tab.accounts")}</Text>
                        <Text visibleFrom="xs" size="md">{t("dashboard.tab.accounts")}</Text>
                      </Tabs.Tab>
                    </Tabs.List>
                    <Tabs.Panel value="synchronizations">
                      <SynchronizationsTab accounts={accounts} openTab={openTab} />
                    </Tabs.Panel>
                    <Tabs.Panel value="accounts">
                      <AccountTab accounts={accounts} updateAccounts={updateAccounts} />
                    </Tabs.Panel>
                    <Tabs.Panel value="schedules">
                      <ScheduleTab accounts={accounts} openTab={openTab} />
                    </Tabs.Panel>
                  </Tabs>
                ) : (
                  <>
                    <div>
                      <div className="alert alert-success" role="alert">
                        <Stack gap="4">
                          <span className='fw-bold'> {t("dashboard.welcome")} 🎉 </span>
                          {t("dashboard.first-thing")}
                        </Stack>
                      </div>
                    </div>
                    <AddCalendarAccountWrapper user={user} accounts={accounts} updateAccounts={updateAccounts} />
                  </>
                )}
              </>
            )}
          </>
        }
      </div>
    </>
  )
};

const Dashboard: React.FC = () => {
  return <AppLayout>
    <Head>
      <title>Dashboard | Calensync</title>
      <meta property="og:title" content='Dashboard | Calensync' />
      <meta property="og:url" content={`https://calensync.live${PUBLIC_URL}`} />
      <meta property="og:description" content='Create your synchronization rules between your Google and Outlook calendars' />
    </Head>
    <InnerDashboard />
  </AppLayout>
}

export default Dashboard;